import {observable, action, computed} from 'mobx'
import moment from 'moment'
import {mergeDeep} from 'Utils'
import {notification} from 'antd'
import {Indicator} from './indicator'
import {Compare} from './compare'

export class Analysis {
  $key: string;
  $value: any;
  
  @observable loading = false
  @observable has_coins = false
  @observable ticker = ''
  @observable tickerName = ''
  @observable company = ''

  @observable bar_size = 'daily'
  @observable start_dt = moment().subtract(6, 'months')
  @observable end_dt = moment()
  @observable period = '6m'

  @observable supportedParts = []
  @observable technical = []
  @observable fundamental = []
  @observable sentiment = []
  @observable compare = {}

  @observable rawChartData = {}
  @observable chartData = []

  constructor(rootStore, api) {
    this.root = rootStore
    this.api = api
    this.technical = [new Indicator(rootStore, 'RSI', 'technical')]
  }

  @computed get indicators() {
    return this.technical.concat(this.fundamental, this.sentiment)
  }

  @action change(key: string, value: any) {
    this[key] = value
  }

  @action calculateChartData() {
    let array = []
    for (let label in this.rawChartData) {
      array.push(this.rawChartData[label])
    }
    this.indicators.map(ind => {
      array.push(ind.data)
    })
    if (this.compare instanceof Compare) {
      array.push(this.compare.data)
    }
    const merged = mergeDeep({}, ...array)
    const dates = Object.keys(merged) || []
    let data = []
    dates.map((date) => {
      const obj = merged[date]
      data.push({
        date,
        ...obj
      })
    })
    this.chartData = data
  }

  @action changeBarSize(value) {
    this.bar_size = value
    switch(value) {
    case 'monthly':
      this.start_dt = moment(this.end_dt).subtract(3, 'years')
      break
    case 'weekly':
      this.start_dt = moment(this.end_dt).subtract(1, 'years')
      break
    case 'daily':
      this.start_dt = moment(this.end_dt).subtract(6, 'months')
      break
    case 'hourly':
      this.start_dt = moment(this.end_dt).subtract(7, 'days')
      break
    case '10-minute':
      this.start_dt = moment(this.end_dt).subtract(1, 'days')
      break
    case 'minute':
      this.start_dt = moment(this.end_dt).subtract(6, 'hours')
      break
    }
  }

  @action setPeriod(value) {
    this.period = value
    this.end_dt = moment()
    switch(value) {
    case 'today':
      this.start_dt = moment().subtract(24, 'hours')
      break
    case '2d':
      this.start_dt = moment().subtract(2, 'days')
      break
    case '5d':
      this.start_dt = moment().subtract(5, 'days')
      break
    case '1m':
      this.start_dt = moment().subtract(1, 'months')
      break
    case '3m':
      this.start_dt = moment().subtract(3, 'months')
      break
    case '6m':
      this.start_dt = moment().subtract(6, 'months')
      break
    case 'ytd':
      this.start_dt = moment().set({'month': 0, 'date': 1})
      break
    case '1y':
      this.start_dt = moment().subtract(1, 'years')
      break
    case '2y':
      this.start_dt = moment().subtract(2, 'years')
      break
    case '5y':
      this.start_dt = moment().subtract(5, 'years')
      break
    case '10y':
      this.start_dt = moment().subtract(10, 'years')
      break
    case 'max':
      this.start_dt = moment().subtract(10, 'years')
      break
    }
  }

  @action compareTickers(ticker) {
    if (this.compare instanceof Compare) {
      this.compare.setTicker(ticker)
    } else {
      this.compare = new Compare(this.root, ticker)
    }
    this.compare.load()
  }

  @action addIndicator(type, ident) {
    const ind = new Indicator(this.root, ident, type)
    this[type].push(ind)
    if (this.ticker) {
      ind.load()
    }
  }

  @action removeIndicator = (type, ident) => {
    this[type] = this[type].filter(ind => ind.ident !== ident)
  }

  @action async prepareReport(body) {
    try {
      return await this.api.request('sys/data/analysis-lab/prepare-report', 'post', body)
    } catch (e) {
      notification['error']({
        message: 'Failed to load report data',
        desription: e.message || 'Unknown error'
      })
      return Promise.reject()
    }
  }

  @action async loadCandlestick() {
    if (!this.ticker) {
      return
    }
    this.loading = true
    this.chartData = []
    this.rawChartData = {}
    const indicators = this.indicators.reduce((filtered, ind) => {
      if (ind.type !== 'compare') {
        filtered.push(ind.syntax_parts)
      }
      return [].concat.apply([], filtered)
    }, [])

    const body = {
      reports: [{
        start_dt: this.start_dt.toISOString(),
        end_dt: this.end_dt.toISOString(),
        bar_size: this.bar_size,
        tickers: [this.ticker],
        syntax_parts: [
          {ident:'OPEN_PRICE',params:{}},
          {ident:'CLOSE_PRICE',params:{}},
          {ident:'HIGH_PRICE',params:{}},
          {ident:'LOW_PRICE',params:{}},
          {ident:'VOLUME',params:{}}
        ].concat(indicators)
      }]
    }
    try {
      const resObj = await this.prepareReport(body)
      let data = {}
      resObj.reports[0].syntax_part_reports_data.map(row => {
        if (row.syntax_part.ident === 'BB') {
          return
        }
        if (['OPEN_PRICE', 'CLOSE_PRICE', 'HIGH_PRICE', 'LOW_PRICE', 'VOLUME'].indexOf(row.syntax_part.ident) !== -1) {
          data[row.label] = this.processTickerData(row)
        } else {
          let indicator = this.indicators.find(ind => ind.ident === row.syntax_part.ident)
          if (!indicator) {
            return
          }
          indicator.data = this.processTickerData(row)
        }
      })

      const upperData = resObj.reports[0].syntax_part_reports_data.find(row => row.label.indexOf('upper') !== -1 && row.syntax_part.ident === 'BB')
      const middleData = resObj.reports[0].syntax_part_reports_data.find(row => row.label.indexOf('middle') !== -1 && row.syntax_part.ident === 'BB')
      const lowerData = resObj.reports[0].syntax_part_reports_data.find(row => row.label.indexOf('lower') !== -1 && row.syntax_part.ident === 'BB')

      if (upperData && middleData && lowerData) {
        const ts = upperData.data.ts
        const upper = upperData.data.values
        const middle = middleData.data.values
        const lower = lowerData.data.values

        let bbData = {}
        for (let i=0; i < upper.length; i++) {
          bbData[ts[i]] = {
            bb: {
              top: upper[i],
              middle: middle[i],
              bottom: lower[i]
            }
          }
        }
        let indicator = this.indicators.find(ind => ind.ident === 'BB')
        indicator.data = bbData
      }
      this.rawChartData = data
      this.calculateChartData()
      this.indicators.map(ind => {
        ind.loading = false
        ind.loaded = true
      })
    } finally {
      this.loading = false
    }
  }

  @action processTickerData(tickerData, synt) {
    const {data, syntax_part} = tickerData
    let syntax
    if (!synt) {
      switch(syntax_part.syntax) {
      case '52 week high':
        syntax = 'h52'
        break
      case '52 week low':
        syntax = 'l52'
        break
      case 'moving average':
        syntax = 'ma'
        break
      case 'exponential moving average':
        syntax = 'ema'
        break
      default:
        syntax = syntax_part.syntax
      }
      syntax = syntax.split(' ').join('_').toLowerCase()
    } else {
      syntax = synt
    }

    let processed = {}
    data.ts.map((ts, index) => {
      let obj = {}
      obj[syntax] = data.values[index]
      processed[ts] = obj
    })
    return processed
  }

  @action async getSupportedParts() {
    try {
      const {alternatives, fundamentals, indicators} = await this.api.request('sys/signal/supported-parts', 'get')
      this.supportedParts = alternatives.concat(fundamentals, indicators)
    } catch (e) {
      notification['error']({
        message: 'Failed to load indicator settings',
        description: e.message || 'Unknown error'
      })
    }
  }

  @action restore() {
    this.technical = [new Indicator(this.root, 'RSI', 'technical')]
    this.fundamental = []
    this.sentiment = []
    this.compare = {}
  }
}
